/**
 * crmStages.js - the CRM lead pipeline and its picklists (Paul Sarantos'
 * Phase 1.2 spec, answered 9/28/26).
 *
 * Five hand-set stages, from first two-way contact to a signed NDA. Quotes
 * live on Opportunities, and customers are not leads: a site whose
 * Designation is Customer leaves the Leads board (Paul: "current/active
 * customers shouldn't be in the leads pipeline, only new pursuers").
 *
 * Server twin: server/lib/crm.js (keep the lists in sync - server/test_crm.js
 * checks they match).
 */
export const CRM_STAGES = [
  'Contacted / Nurturing',
  'Discovery Meeting Scheduled',
  'Discovery Meeting Complete',
  'NDA In-Process',
  'NDA Executed',
];

/** Kanban columns: the whole hand-set pipeline. */
export const KANBAN_STAGES = CRM_STAGES;

/** New records start here. Picking a stage is still required on create (Paul, Q8). */
export const DEFAULT_STAGE = 'Contacted / Nurturing';

export const STAGE_DEFINITIONS = {
  'Contacted / Nurturing': 'Two-way contact made (replied, asked for information). No meeting booked yet.',
  'Discovery Meeting Scheduled': 'First meeting is on the calendar.',
  'Discovery Meeting Complete': 'First meeting held - needs a logged discovery call or site visit.',
  'NDA In-Process': 'Our NDA sent, or theirs received, awaiting signature.',
  'NDA Executed': 'NDA signed.',
};

/** Moving a lead to this stage needs a logged meeting of one of MEETING_GATE_TYPES (L-09). */
export const MEETING_GATE_STAGE = 'Discovery Meeting Complete';
/** Paul (Q13): "Teams call or onsite meeting". */
export const MEETING_GATE_TYPES = ['Discovery call', 'Site visit'];

export const stageRank = (s) => CRM_STAGES.indexOf(s);

export const STAGE_COLORS = {
  'Contacted / Nurturing': '#0288D1',
  'Discovery Meeting Scheduled': '#5E35B1',
  'Discovery Meeting Complete': '#7B1FA2',
  'NDA In-Process': '#E65100',
  'NDA Executed': '#2E7D32',
  // legacy names, so a record the migration has not reached still renders
  'Target - no contact': '#9E9E9E',
  'Contact attempted': '#0288D1',
  'Discovery meeting complete': '#7B1FA2',
  'NDA sent': '#E65100',
  'NDA executed': '#2E7D32',
  'RFQ received': '#2E7D32',
  'Active customer': '#1B5E20',
};

/**
 * Designation (A-01): only a PO makes someone a Customer; a Customer never
 * goes back to Lead (Paul, Q1). Account (Paul, 10/21/26): a company or site we
 * track that is not in the lead pipeline and not a Customer yet - an end-user
 * site made to attach an opportunity, a long-standing relationship. It is off
 * the Leads board and out of every lead count; a won opportunity still makes
 * it a Customer, exactly as it does a Lead. Target (Paul, T-0010, 11/3/26):
 * upstream of Lead - a company or site to pursue before a good contact is
 * engaged (a Lead can be set back to Target). Off the Leads board and lead
 * counts like Account; set it to Lead once someone good is engaged.
 */
export const DESIGNATIONS = ['Target', 'Lead', 'Account', 'Customer'];
/** Tracked but not in the lead pipeline: off the Leads board and every lead count. */
export const NOT_LEAD_DESIGNATIONS = ['Target', 'Account'];
export const DESIGNATION_DEFINITIONS = {
  Target:
    'A company or site to pursue, no good contact engaged yet. Off the Leads board and out of lead counts; set it to Lead once one is.',
  Lead: 'A business-development lead: contact made, no PO yet. On the Leads board.',
  Account:
    'Tracked, but not in the lead pipeline and not a Customer yet (e.g. an end-user site for an opportunity). Off the Leads board and out of lead counts.',
  Customer: "We have a PO from them. A Customer can't be set back to Target, Lead or Account.",
};

/** Status (A-03), set site by site (Paul, Q4). */
export const LEAD_STATUSES = ['Active', 'Cold', 'Inactive', 'Dead'];
export const LEAD_STATUS_DEFINITIONS = {
  Active: 'Able to do business.',
  Cold: 'Had meetings or discussions, but no response now. Still a fit; revisit later.',
  Inactive: 'Bad fit, or a company we choose not to work with.',
  Dead: 'The site or company has shut down permanently.',
};
export const LEAD_STATUS_COLORS = { Active: '#2E7D32', Cold: '#0277BD', Inactive: '#757575', Dead: '#424242' };

/** A-05. Picking one is required when creating an account or site (Paul, Q8). */
export const LEAD_SOURCES = [
  'GushWork',
  'InsideJob',
  'LinkedIn Ads',
  'Website Inquiry',
  'FANUC',
  'Other Suppliers',
  'Trade Show',
  'Network / Referral',
  'Other',
];

export const NDA_STATUSES = ['Not started', 'Sent', 'Received', 'Executed', 'Expired'];
/** L-04 card badge: Not started / Expired red, Sent / Received yellow, Executed green, blank none. */
export const NDA_BADGE = {
  'Not started': { bg: '#FFEBEE', fg: '#B71C1C' },
  Expired: { bg: '#FFEBEE', fg: '#B71C1C' },
  Sent: { bg: '#FFF8E1', fg: '#8A6D00' },
  Received: { bg: '#FFF8E1', fg: '#8A6D00' },
  Executed: { bg: '#E8F5E9', fg: '#1B5E20' },
};

export const ROBOT_BRANDS = ['FANUC', 'ABB', 'KUKA', 'Yaskawa', 'Kawasaki', 'Universal', 'None', 'Unknown'];

/** Activity types. AL-05 added the six steps between first meeting and PO. */
export const MEETING_TYPES = [
  'Discovery call',
  'Follow-up call',
  'Teams meeting',
  'Site visit',
  'Trade show',
  'Email thread',
  'NDA sent / received',
  'Document sent',
  'RFQ received',
  'Budgetary sent',
  'Proposal sent',
  'Customer Q&A',
  'Other',
];
/** The type that carries a revision number (AL-05: "Proposal sent (with revision #)"). */
export const REVISION_TYPE = 'Proposal sent';

/** C-03 - who holds which part of the buying decision. The old free-text "role" is now "Title". */
export const CONTACT_ROLES = ['Decision maker', 'Engineering', 'Maintenance', 'Procurement', 'Champion'];

/** C-04 - a contact who left keeps their history but drops out of every picker. */
export const CONTACT_STATUSES = ['Active', 'Left company'];
export const LEFT_COMPANY = 'Left company';
export const isLeftCompany = (c) => pickField(c, 'contactStatus', 'contact_status') === LEFT_COMPANY;

/** Days a record has sat in its current stage (null when never staged). */
export const daysInStage = (acct) => {
  const t = acct.stageChangedAt || acct.stage_changed_at;
  if (!t) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(t).getTime()) / 86400000));
};

/**
 * Tier vocabulary is A / B / C, off Paul's target scoring model (A >= 70,
 * B >= 50). The old 'Tier 1 - Strategic' style values are mapped on read so
 * nothing already stored breaks; normalizeTier is the single place that rule
 * lives. Tier is an ACCOUNT-level attribute - a Site inherits its parent's.
 */
export const TIERS = ['A', 'B', 'C'];
export const TIER_HINT = { A: 'score 70+', B: 'score 50-69', C: 'below 50' };
export const normalizeTier = (t) => {
  const s = String(t || '').trim();
  if (!s) return '';
  const u = s.toUpperCase();
  if (/^(TIER\s*)?A\b/.test(u) || /TIER\s*1\b/.test(u)) return 'A';
  if (/^(TIER\s*)?B\b/.test(u) || /TIER\s*2\b/.test(u)) return 'B';
  if (/^(TIER\s*)?C\b/.test(u) || /TIER\s*3\b/.test(u)) return 'C';
  return s;
};
export const tierFromScore = (n) => {
  const v = parseFloat(n);
  if (!isFinite(v)) return '';
  return v >= 70 ? 'A' : v >= 50 ? 'B' : 'C';
};

/**
 * Vertical (A-07) - set on the Account by what the company primarily makes.
 * A site inherits its company's vertical unless that site has its own
 * (X-07, 10/1/26: e.g. one plant builds data center racks); the server sends
 * the result as verticalEffective.
 */
export const VERTICALS = [
  'Automotive OEM',
  'Automotive Supplier',
  'Heavy Truck, Bus & Rail',
  'Construction & Agricultural Equipment',
  'Powersports, Marine & Outdoor Power',
  'Aerospace & Defense',
  'Electrical, Grid & Data Center Equipment',
  'Battery & Energy Storage',
  'Appliances, HVAC & Food Equipment',
  'Building Products & Furniture',
  'Medical Devices & Equipment',
  'Industrial & Process Equipment',
  'Other',
];

/** Suggested Details field names (Paul #40) so 'Processes' vs 'Processes present' stops happening. */
export const DETAIL_FIELD_SUGGESTIONS = [
  'Plant type',
  'Processes present',
  'Buying autonomy',
  'Approximate headcount',
  'Robot count',
  'Paint shop',
  'Sealer line',
  'Union / non-union',
  'Fiscal year end',
];

/** Read a field whichever casing it arrived in (AppContext camelCases nested rows too; raw API rows are snake_case). */
export const pickField = (o, camel, snake) => (o ? (o[camel] !== undefined ? o[camel] : o[snake]) : undefined);

/** Effective designation / vertical / status as the server computed them (with safe fallbacks). */
export const designationOf = (a) => pickField(a, 'designationEffective', 'designation_effective') || a.designation || 'Lead';
export const verticalOf = (a) => pickField(a, 'verticalEffective', 'vertical_effective') ?? a.vertical ?? null;
export const leadStatusOf = (a) => pickField(a, 'leadStatus', 'lead_status') || 'Active';

/**
 * Made automatically for an opportunity (crm_accounts.auto_created, Sharif
 * 10/12/26): an account with an owner, but NOT a lead someone is working, so
 * the Leads board, the Leads Dashboard and the next-step nags leave it out.
 * "Track as a lead" (or a Stage / Status change) takes the flag off.
 */
export const isAutoCreated = (a) => !!pickField(a, 'autoCreated', 'auto_created');
/** The designation as stored (what the select shows), Lead when blank. */
export const storedDesignationOf = (a) => (a && a.designation) || 'Lead';
/**
 * Account, not a lead (Paul, 10/21/26): its effective designation is Account,
 * or it is stored as Account (an Account a win made a Customer was never a
 * lead either, so the Leads Dashboard leaves it out too).
 */
export const isNotLeadDesignation = (a) =>
  !!a && (NOT_LEAD_DESIGNATIONS.includes(designationOf(a)) || NOT_LEAD_DESIGNATIONS.includes(storedDesignationOf(a)));
/** A Lead by designation that is not tracked as one (the chip and the "Track as a lead" note). */
export const isUntrackedLead = (a) => !!a && isAutoCreated(a) && designationOf(a) === 'Lead';

/**
 * The Leads board's records: sites plus site-less accounts (a parent with
 * sites is not a lead - its sites are), that are still Leads and tracked as
 * leads (not auto_created). An Account (10/21/26) is never on it: its
 * effective designation is Account, or Customer once a win makes it one. `cold` picks the Cold view; Inactive and Dead
 * never show (L-03).
 */
export const isBoardUnit = (a) => !!a.parentAccountId || !((a.siteCount || a.site_count || 0) > 0);
export const onLeadsBoard = (a, cold = false) =>
  isBoardUnit(a) && !isAutoCreated(a) && designationOf(a) === 'Lead' && leadStatusOf(a) === (cold ? 'Cold' : 'Active');
